import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { withRedirect } from '@/lib/authRedirect';

export const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading, authUnavailable, checkAuth } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  // Session not checkable (offline / server error): not a sign-out, so no
  // redirect to /login. The check is retried automatically.
  if (!user && authUnavailable) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" role="status" data-testid="auth-unavailable">
        <div className="max-w-sm text-center space-y-4">
          <h1 className="text-xl font-semibold">{t('auth.unavailable.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('auth.unavailable.body')}</p>
          <button type="button" onClick={() => checkAuth()} data-testid="auth-unavailable-retry"
            className="px-4 py-2 rounded-md bg-accent text-white">{t('auth.unavailable.retry')}</button>
        </div>
      </div>
    );
  }

  if (!user) {
    // Back to the requested page after signing in (e.g. a listing to correct,
    // /dashboard?editService=…): only allowed Ridalo pages (safeRedirect).
    return <Navigate to={withRedirect('/login', `${location.pathname}${location.search}${location.hash}`)} state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};